import React, { useState, useEffect } from 'react';
import { ClockIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import inventoryApi from '../../../services/inventoryApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import CommonDataTable from '../../Common/CommonDataTable';

const formatCurrency = (amount) => `Rs ${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BUCKET_ORDER = ['0-30', '31-60', '61-90', '91-180', '180+'];
const BUCKET_COLORS = {
  '0-30': '#16a34a',
  '31-60': '#ca8a04',
  '61-90': '#ea580c',
  '91-180': '#dc2626',
  '180+': '#7f1d1d',
};
const BUCKET_BADGE_CLASSES = {
  '0-30': 'bg-green-100 text-green-800',
  '31-60': 'bg-yellow-100 text-yellow-800',
  '61-90': 'bg-orange-100 text-orange-800',
  '91-180': 'bg-red-100 text-red-800',
  '180+': 'bg-red-200 text-red-900',
};

const CustomBarTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const p = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
        <p className="font-medium text-gray-900">{p.bucket} days</p>
        <p className="text-gray-600">{formatCurrency(p.value)}</p>
        <p className="text-gray-400 text-xs">{p.itemCount} items</p>
      </div>
    );
  }
  return null;
};

const StockAgingReport = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState([]);
  const [bucketBreakdown, setBucketBreakdown] = useState([]);
  const [summary, setSummary] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);

  const [filters, setFilters] = useState({
    warehouseId: '',
    categoryId: '',
    minDays: '',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadAging();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const [warehousesRes, categoriesRes] = await Promise.all([
        inventoryApi.getWarehouses(),
        inventoryApi.getCategories({ limit: 1000 })
      ]);
      setWarehouses(warehousesRes.data?.data || warehousesRes.data || []);
      setCategories(categoriesRes.data?.data || categoriesRes.data || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const buildParams = () => {
    const params = {};
    if (filters.warehouseId) params.warehouseId = filters.warehouseId;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.minDays) params.minDays = filters.minDays;
    return params;
  };

  const loadAging = async () => {
    setLoading(true);
    try {
      const response = await inventoryApi.getStockAgingReport(buildParams());
      const data = response?.data || response;

      setItems(data.items || []);
      setBucketBreakdown(data.bucketBreakdown || []);
      setSummary(data.summary || {});
    } catch (error) {
      console.error('Error loading stock aging report:', error);
      setItems([]);
      setBucketBreakdown([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ warehouseId: '', categoryId: '', minDays: '' });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await inventoryApi.getStockAgingReportCSV(buildParams());
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock-aging-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setExporting(false);
    }
  };

  const bucketChartData = BUCKET_ORDER.map(bucket => {
    const row = bucketBreakdown.find(b => b.bucket === bucket) || { itemCount: 0, totalValue: 0 };
    return {
      bucket,
      value: row.totalValue,
      itemCount: row.itemCount,
      color: BUCKET_COLORS[bucket],
    };
  });

  const columns = [
    {
      key: 'itemName',
      header: 'Item Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-semibold text-gray-900">{row.itemName}</div>
          <div className="text-sm text-gray-500">SKU: {row.sku}</div>
        </div>
      )
    },
    { key: 'category', header: 'Category', sortable: true },
    { key: 'warehouseName', header: 'Warehouse', sortable: true },
    {
      key: 'quantity',
      header: 'Quantity',
      sortable: true,
      render: (value, row) => <div className="text-right">{row.quantity} {row.unit}</div>
    },
    {
      key: 'totalValue',
      header: 'Total Value',
      sortable: true,
      render: (value, row) => <div className="text-right font-semibold">{formatCurrency(row.totalValue)}</div>
    },
    {
      key: 'lastMovement',
      header: 'Last Movement',
      sortable: true,
      render: (value, row) => row.lastMovement ? new Date(row.lastMovement).toLocaleDateString() : '-'
    },
    {
      key: 'agingDays',
      header: 'Aging',
      sortable: true,
      render: (value, row) => <div className="text-right">{row.agingDays} days</div>
    },
    {
      key: 'agingBucket',
      header: 'Bucket',
      render: (value, row) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${BUCKET_BADGE_CLASSES[row.agingBucket] || 'bg-gray-100 text-gray-800'}`}>
          {row.agingBucket} days
        </span>
      )
    },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Filters Card */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-5 py-3 flex items-center gap-3 rounded-t-xl">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <ClockIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Stock Aging Report</h2>
              <p className="text-indigo-200 text-xs">How long stock on hand has been sitting since its last movement</p>
            </div>
          </div>

          <div className="px-5 py-4 flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Warehouse</label>
              <select
                value={filters.warehouseId}
                onChange={(e) => handleFilterChange('warehouseId', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[180px]"
              >
                <option value="">All Warehouses</option>
                {warehouses.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</label>
              <select
                value={filters.categoryId}
                onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[180px]"
              >
                <option value="">All Categories</option>
                {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Min Aging (Days)</label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 90"
                value={filters.minDays}
                onChange={(e) => handleFilterChange('minDays', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors w-40"
              />
            </div>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-800 transition-colors flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 shadow-sm"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner size="lg" fullScreen={false} />
        </div>
      )}

      {/* Content */}
      {!loading && (
        <div className="flex-grow overflow-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="space-y-4">
            {summary && Object.keys(summary).length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Items</h3>
                  <p className="text-2xl font-bold text-indigo-600">{summary.totalItems || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Quantity</h3>
                  <p className="text-2xl font-bold text-gray-800">{summary.totalQuantity || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Value</h3>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.totalValue)}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Oldest Stock</h3>
                  <p className="text-2xl font-bold text-red-600">{summary.oldestAgingDays || 0} days</p>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Value by Aging Bucket</h3>
              <p className="text-xs text-gray-400 mb-4">Inventory value grouped by days since last movement</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={bucketChartData} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomBarTooltip />} cursor={{ fill: '#f9fafb' }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={60}>
                    {bucketChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-indigo-600 px-4 py-3 flex items-center justify-between">
                <span className="text-sm font-medium text-white">Aging Details ({items.length})</span>
              </div>
              <CommonDataTable
                data={items}
                columns={columns}
                searchable={true}
                searchPlaceholder="Search items..."
                emptyMessage="No stock data available"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockAgingReport;
